"use client";

import { type ModuleTeamGrid } from "@lib/types";

import PortableText from "@/components/shared/portable-text";
import { Badge } from "@/components/ui/badge";
import { Container } from "@/components/ui/container";
import { cn } from "@/lib/ui/cn";
import { getModuleColorTheme } from "@/lib/ui/colorTheme";

import { TeamGridCard } from "./TeamGrid/Card";

type TeamGridModuleProps = {
  module: ModuleTeamGrid;
};

export function TeamGridModule({ module }: TeamGridModuleProps) {
  const { themeTextClasses, isDarkTheme, proseClasses } = getModuleColorTheme(module);

  if (!module.items || module.items.length === 0) return null;

  const eyebrowBadge = module.eyebrow ? (
    <Badge variant="success" className="w-fit">
      {module.eyebrow}
    </Badge>
  ) : null;

  return (
    <Container
      as="section"
      className={cn(
        "from-neutral-darkest via-indigo-darkest flex flex-col gap-8 bg-gradient-to-b to-gray-950",
        themeTextClasses,
        "py-padding-section-lg",
      )}
    >
      <div className="flex flex-col gap-6">
        <div className="max-w-3xl space-y-3">
          {eyebrowBadge}
          {module.title ? (
            <h3
              className={cn(
                "typography-heading-3",
                isDarkTheme ? "text-white" : "text-neutral-darkest",
              )}
            >
              {module.title}
            </h3>
          ) : null}
          {module.description ? (
            <PortableText
              className={cn("typography-text-large-normal prose prose-p:my-0 flex-1", proseClasses)}
              value={module.description}
            />
          ) : null}
        </div>

        <div className="gap-gap-element-lg tablet:grid-cols-3 desktop:grid-cols-4 grid grid-cols-2">
          {module.items.map((item) => (
            <TeamGridCard key={item._key} item={item} textColorClasses={proseClasses} />
          ))}
        </div>
      </div>
    </Container>
  );
}
